import React from 'react'
import { Divider, IconButton, List } from '@mui/material'
import { SideDrawerElements } from './SideDrawerElements'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { SideDrawerProps } from './SideDrawerTypes'
import SideDrawerLink from './components/SideDrawerLink'
import GridViewIcon from '@mui/icons-material/GridView'
import PostAddIcon from '@mui/icons-material/PostAdd'
import LinkIcon from '@mui/icons-material/Link'
import { usePageNavigation } from 'hooks/useNavigation'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import AccountBoxIcon from '@mui/icons-material/AccountBox'
import TravelExploreIcon from '@mui/icons-material/TravelExplore'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest'
import AssessmentIcon from '@mui/icons-material/Assessment'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import DrawerStyle from './Style'
import { useIsAdmin } from 'hooks/useIsAdmin'

export default function SideDrawer({
  open,
  setOpen,
  isMobile,
}: SideDrawerProps): JSX.Element {
  const {
    navigateToDashboard,
    navigateToListeningTopics,
    navigateToAnalytics,
    navigateToAccounts,
    navigateToPosts,
    navigateToTargetAudience,
    navigateToAutomations,
    navigateToSubscriptions,
    navigateToProfile,
    navigateToAdmin,
  } = usePageNavigation()

  const { isAdmin } = useIsAdmin()

  const handleNavClick = (navigateFn: () => void) => {
    navigateFn()
    if (isMobile) {
      setOpen(false)
    }
  }

  return (
    <SideDrawerElements.Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={open}
      onClose={() => setOpen(false)}
    >
      {/* Header with close button */}
      <SideDrawerElements.DrawerHeader>
        <IconButton 
          onClick={() => setOpen(false)}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            transition: 'all 0.2s',
            '&:hover': {
              color: '#a855f7',
              backgroundColor: 'rgba(168, 85, 247, 0.1)',
              transform: 'scale(1.1)',
            }
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      </SideDrawerElements.DrawerHeader>

      {/* Divider with gradient */}
      <Divider 
        sx={{ 
          borderColor: 'rgba(168, 85, 247, 0.2)',
          boxShadow: '0 1px 3px rgba(168, 85, 247, 0.1)',
        }} 
      />

      <DrawerStyle.DrawerWrapper>
        <List
          sx={{
            width: '100%',
            padding: '8px',
            // Selected state - purple gradient
            '&& .Mui-selected, && .Mui-selected:hover': {
              background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
              borderRight: '3px solid #a855f7',
              borderRadius: '8px',
              backdropFilter: 'blur(12px)',
              '&, & .MuiListItemIcon-root': {
                color: '#a855f7',
              },
              '& .MuiListItemText-primary': {
                fontWeight: 600,
              }
            },
            // Hover states - subtle purple glow
            '& .MuiListItemButton-root:hover': {
              background: 'rgba(168, 85, 247, 0.08)',
              borderRadius: '8px',
              transform: 'translateX(4px)',
              transition: 'all 0.2s',
              '&, & .MuiListItemIcon-root': {
                color: '#c084fc',
              },
            },
            '& .MuiListItemButton-root': {
              borderRadius: '8px',
              marginBottom: '4px',
              transition: 'all 0.2s',
              '& .MuiListItemIcon-root': {
                color: 'rgba(255, 255, 255, 0.6)',
                minWidth: '40px',
              },
              '& .MuiListItemText-primary': {
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
              }
            },
          }}
        >
          <SideDrawerLink
            open={open}
            text={'Dashboard'}
            icon={<GridViewIcon />}
            onClick={() => handleNavClick(navigateToDashboard)}
            path="dashboard"
          />
          <SideDrawerLink
            open={open}
            text={'Social Radar'}
            icon={<GraphicEqIcon />}
            onClick={() => handleNavClick(navigateToListeningTopics)}
            path="topics"
          />
          <SideDrawerLink
            open={open}
            text={'Analytics'}
            icon={<AssessmentIcon />}
            onClick={() => handleNavClick(navigateToAnalytics)}
            path="analytics"
          />
          <SideDrawerLink
            open={open}
            text={'Posts'}
            icon={<PostAddIcon />}
            onClick={() => handleNavClick(navigateToPosts)}
            path="posts-manage"
          />
          {/* Gradient Divider */}
          <Divider
            sx={{
              margin: '12px 0',
              borderColor: 'rgba(168, 85, 247, 0.2)',
              boxShadow: '0 1px 3px rgba(168, 85, 247, 0.1)',
            }}
          />

          <SideDrawerLink
            open={open}
            text={'Accounts'}
            icon={<LinkIcon />}
            onClick={() => handleNavClick(navigateToAccounts)}
            path="accounts"
          />
          <SideDrawerLink
            open={open}
            text={'Subscriptions'}
            icon={<CreditCardIcon />}
            onClick={() => handleNavClick(navigateToSubscriptions)}
            path="subscription"
          />
          <SideDrawerLink
            open={open}
            text={'Profile'}
            icon={<AccountBoxIcon />}
            onClick={() => handleNavClick(navigateToProfile)}
            path="profile"
          />
          {isAdmin && (
            <>
              <Divider
                sx={{
                  margin: '12px 0',
                  borderColor: 'rgba(168, 85, 247, 0.2)',
                  boxShadow: '0 1px 3px rgba(168, 85, 247, 0.1)',
                }}
              />
              <SideDrawerLink
                open={open}
                text={'Admin'}
                icon={<AdminPanelSettingsIcon />}
                onClick={() => handleNavClick(navigateToAdmin)}
                path="admin"
              />
            </>
          )}
          <SideDrawerElements.Spacer />
        </List>

        {/* Modern Logo */}
        {open ? (
          <DrawerStyle.LogoWrapper>
            <DrawerStyle.Logo>NALAPOD</DrawerStyle.Logo>
          </DrawerStyle.LogoWrapper>
        ) : null}
      </DrawerStyle.DrawerWrapper>
    </SideDrawerElements.Drawer>
  )
}